import { Loader2, Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  discussoesControllerObterArquivoDaMensagem,
  useDiscussoesControllerRetranscreverMensagemDeVoz,
} from '@/api/generated/discussões-técnicas/discussões-técnicas';
import type { MensagemDeVozResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { formatarDuracao } from '@/shared/hooks/useGravadorDeAudio';
import { toast } from '@/shared/ui/sonner';

/** Acima disso a transcrição abre recolhida, com "Ver mais" — como no WhatsApp. */
const LIMITE_TRANSCRICAO_RECOLHIDA = 220;

interface Props {
  discussaoId: string;
  mensagemId: string;
  voz: MensagemDeVozResponse;
  /** Depois de "Transcrever" dar certo — a tela recarrega a lista para os dois lados verem. */
  aoTranscrever: () => void;
}

/**
 * Balão de mensagem de voz: player + transcrição abaixo.
 *
 * O áudio só é buscado no primeiro ▶ (URL assinada via a rota de arquivo da
 * mensagem): com o polling de 2 s do chat, assinar a URL de todo áudio da
 * conversa a cada render seria desperdício. Se a URL expirar entre um play e
 * outro, o erro do `<audio>` limpa o cache e o próximo ▶ busca de novo.
 *
 * O progresso usa `voz.duracaoSegundos`, não `audio.duration`: o WebM do
 * `MediaRecorder` do Chrome sai sem duração no cabeçalho (`Infinity`).
 */
export function MensagemDeVoz({ discussaoId, mensagemId, voz, aoTranscrever }: Props) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [tocando, setTocando] = useState(false);
  const [posicao, setPosicao] = useState(0);
  const [expandida, setExpandida] = useState(false);
  const tocarQuandoCarregar = useRef(false);

  const retranscrever = useDiscussoesControllerRetranscreverMensagemDeVoz({
    mutation: {
      onSuccess: aoTranscrever,
      onError: (erro) =>
        toast.error(
          erro instanceof ApiError ? erro.message : 'Não foi possível transcrever o áudio agora.',
        ),
    },
  });

  const duracao = Math.max(1, voz.duracaoSegundos);

  useEffect(() => {
    if (url && tocarQuandoCarregar.current) {
      tocarQuandoCarregar.current = false;
      void audioRef.current?.play().catch(() => setTocando(false));
    }
  }, [url]);

  async function alternar() {
    const audio = audioRef.current;
    if (tocando) {
      audio?.pause();
      return;
    }
    if (url) {
      void audio?.play().catch(() => setTocando(false));
      return;
    }

    setCarregando(true);
    try {
      const arquivo = await discussoesControllerObterArquivoDaMensagem(discussaoId, mensagemId);
      if (!arquivo.url) {
        toast.error('O áudio desta mensagem não está mais disponível.');
        return;
      }
      tocarQuandoCarregar.current = true;
      setUrl(arquivo.url);
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível carregar o áudio.');
    } finally {
      setCarregando(false);
    }
  }

  const transcricao = voz.transcricao ?? '';
  const longa = transcricao.length > LIMITE_TRANSCRICAO_RECOLHIDA;
  const textoVisivel =
    longa && !expandida
      ? `${transcricao.slice(0, LIMITE_TRANSCRICAO_RECOLHIDA).trimEnd()}…`
      : transcricao;

  return (
    <div className="mt-0.5 min-w-[220px] space-y-1.5">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void alternar()}
          disabled={carregando}
          aria-label={tocando ? 'Pausar mensagem de voz' : 'Ouvir mensagem de voz'}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground hover:bg-brand/90 disabled:opacity-60"
        >
          {carregando ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : tocando ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 translate-x-px" />
          )}
        </button>
        <input
          type="range"
          min={0}
          max={duracao}
          step={0.1}
          value={Math.min(posicao, duracao)}
          onChange={(e) => {
            const valor = Number(e.target.value);
            setPosicao(valor);
            if (audioRef.current && url) audioRef.current.currentTime = valor;
          }}
          disabled={!url}
          aria-label="Posição do áudio"
          className="h-1 flex-1 cursor-pointer accent-brand disabled:cursor-default"
        />
        <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {formatarDuracao(tocando || posicao > 0 ? posicao : duracao)}
        </span>
        {url && (
          <audio
            ref={audioRef}
            src={url}
            preload="auto"
            onPlay={() => setTocando(true)}
            onPause={() => setTocando(false)}
            onEnded={() => {
              setTocando(false);
              setPosicao(0);
            }}
            onTimeUpdate={(e) => setPosicao(e.currentTarget.currentTime)}
            onError={() => {
              setTocando(false);
              setUrl(null);
            }}
          />
        )}
      </div>

      {voz.transcricaoStatus === 'CONCLUIDA' ? (
        <p
          className="text-xs italic text-muted-foreground whitespace-pre-wrap"
          data-testid="transcricao"
        >
          {textoVisivel}
          {longa && (
            <button
              type="button"
              onClick={() => setExpandida((valor) => !valor)}
              className="ml-1 not-italic font-medium text-brand hover:underline"
            >
              {expandida ? 'Ver menos' : 'Ver mais'}
            </button>
          )}
        </p>
      ) : voz.transcricaoStatus === 'SEM_FALA' ? (
        // Sem "Transcrever": a IA já ouviu e não havia fala — tentar de novo
        // só produziria outro texto inventado.
        <p className="text-xs text-muted-foreground">Nenhuma fala identificada neste áudio.</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Transcrição indisponível.{' '}
          <button
            type="button"
            onClick={() => retranscrever.mutate({ id: discussaoId, mensagemId })}
            disabled={retranscrever.isPending}
            className="inline-flex items-center gap-1 font-medium text-brand hover:underline disabled:opacity-60"
          >
            {retranscrever.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
            Transcrever
          </button>
        </p>
      )}
    </div>
  );
}
